import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Primitive } from '@shared/models/common.model';
import { map, tap } from 'rxjs';

@Injectable()
export class ActivatedQueryParamsService {
  groupQueryParams: { [g: string]: Params } = {};
  queryParamGroupMap: { [g: string]: QueryParamMap } = {
    pagination: {
      page: {
        type: 'number',
        default: 1,
      },
      page_size: {
        type: 'number',
        default: 10,
      },
    },
    order: {
      ordering: {
        type: 'string',
        default: '-created_at',
      },
    },
  };

  private activatedRoute = inject(ActivatedRoute);

  addGroup(name: string, queryParamGroupMap: QueryParamMap) {
    this.queryParamGroupMap[name] = queryParamGroupMap;
  }

  addQueryMap(
    name: string,
    queryMap: QueryParamMapItem,
    groupName?: string,
  ) {
    groupName ||= 'default';
    this.queryParamGroupMap[groupName] ||= {};
    this.queryParamGroupMap[groupName][name] = queryMap;
  }

  getQueryParamsByTypeMap(
    queryParams: { [k: string]: string },
    map: QueryParamMap,
  ): Params {
    const convertByType = (
      val: string,
      targetType: string,
    ): Primitive | undefined => {
      if (targetType === 'string') {
        return String(val);
      } else if (targetType === 'boolean') {
        return Boolean(val);
      } else if (targetType === 'number') {
        return Number(val);
      }
      return;
    };
    return Object.keys(map).reduce((obj, key) => {
      const queryValue = queryParams[key];
      const config = map[key];
      if (config == null) {
        return obj;
      }
      if (queryValue == null || queryValue === '') {
        if ('default' in config) {
          obj[key] = config.default;
        }
        return obj;
      } else if (queryValue === 'null') {
        obj[key] = null;
      } else if (config.isArray) {
        obj[key] = (
          Array.isArray(queryValue)
            ? queryValue
            : queryValue.split('|')
        ).map((val) => convertByType(val, config.type));
      } else {
        obj[key] = convertByType(queryValue, config.type);
      }
      return obj;
    }, {} as Params);
  }

  removeGroup(name: string | string[]) {
    if (Array.isArray(name)) {
      name.forEach((n) => delete this.queryParamGroupMap[n]);
    } else {
      delete this.queryParamGroupMap[name];
    }
  }

  get queryParamChanges() {
    return this.activatedRoute.queryParams.pipe(
      map((query) => {
        return {
          originalParams: query,
          paramsByMap: Object.keys(this.queryParamGroupMap).reduce(
            (obj, key) => {
              const groupMap = this.queryParamGroupMap[key];
              obj[key] = this.getQueryParamsByTypeMap(
                query,
                groupMap,
              );
              return obj;
            },
            {} as { [k: string]: Params },
          ),
        };
      }),
      tap((res) => {
        this.groupQueryParams = res.paramsByMap;
      }),
    );
  }

  get queryParams(): Params {
    return Object.values(this.groupQueryParams).reduce(
      (obj, value) => ({ ...obj, ...value }),
      {},
    );
  }
}

export type QueryParamMap = {
  [k: string]: QueryParamMapItem;
};

export type QueryParamMapItem = {
  type: string;
  default?: any;
  isArray?: boolean;
};
